import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-enterprise';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import colDef from '../../../data/grid/coldefManageConditionOrdinary';
import commonColDef from '../../../data/grid/commonColDef';
import useGridOptionsConditionOrdinary from '../../../hooks/grid/gridOptions/useGridOptionsConditionOrdinary';
import useCellClassRulesConditionOrdinary from '../../../hooks/grid/useCellClassRulesConditionOrdinary';
import useColdefConditionOrdinaryDomesticNsc from '../../../hooks/grid/coldef/useColdefConditionOrdinaryDomesticNsc';
import useColdefConditionOrdinaryDomesticAichi from '../../../hooks/grid/coldef/useColdefConditionOrdinaryDomesticAichi';
import useColdefConditionOrdinaryDomesticKobe from '../../../hooks/grid/coldef/useColdefConditionOrdinaryDomesticKobe';
import useColdefConditionOrdinaryDomesticJfe from '../../../hooks/grid/coldef/useColdefConditionOrdinaryDomesticJfe';
import useColdefConditionOrdinaryExportAichi from '../../../hooks/grid/coldef/useColdefConditionOrdinaryExportAichi';
import useColdefConditionOrdinaryExportKobe from '../../../hooks/grid/coldef/useColdefConditionOrdinaryExportKobe';
import useColdefConditionOrdinaryExportNsc from '../../../hooks/grid/coldef/useColdefConditionOrdinaryExportNsc';
import useColdefConditionOrdinaryExportJfe from '../../../hooks/grid/coldef/useColdefConditionOrdinaryExportJfe';
import ActionsManageCondition from '../../cellRenderers/ActionsManageCondition';
import { useTranslation } from 'react-i18next';
import { useDispatch } from '../../../redux/store';
import { openDialog, setDialogMode, setConditionOrdinary } from '../../../redux/slices/condition';
import { BUSINESS_TYPE, CONDITION_VIEW_MODES, STEEL_MAKER } from '../../../constants';
import type { ColDef, GridApi, GridReadyEvent, RowDoubleClickedEvent } from 'ag-grid-community';
import type { ConditionDetailType, ConditionViewMode, ConditionWithDetailTypeClient } from '../../../types/condition';
import type { BusinessType, SteelMaker } from '../../../types/common';

interface GridConditionOrdinaryProps {
  isLoading: boolean;
  selectedViewMode: ConditionViewMode;
  selectedSteelMaker: SteelMaker;
  selectedBusinessType: BusinessType;
  condition: ConditionWithDetailTypeClient;
}

const GridConditionOrdinary = ({ isLoading, selectedViewMode, selectedSteelMaker, selectedBusinessType, condition }: GridConditionOrdinaryProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [gridApi, setGridApi] = useState<GridApi | undefined>(undefined);
  const { gridOptions } = useGridOptionsConditionOrdinary();
  const { cellClassRules } = useCellClassRulesConditionOrdinary();
  const customColDef: ColDef = {
    cellClass: 'cell',
    cellClassRules,
  };
  const defaultColDef: ColDef = Object.assign({}, commonColDef, customColDef);
  const columnDefs: ColDef[] = colDef;
  const colDefDomesticNsc: ColDef[] = useColdefConditionOrdinaryDomesticNsc(t);
  const colDefDomesticAichi: ColDef[] = useColdefConditionOrdinaryDomesticAichi(t);
  const colDefDomesticKobe: ColDef[] = useColdefConditionOrdinaryDomesticKobe(t);
  const colDefDomesticJfe: ColDef[] = useColdefConditionOrdinaryDomesticJfe(t);
  const colDefExportAichi: ColDef[] = useColdefConditionOrdinaryExportAichi(t);
  const colDefExportKobe: ColDef[] = useColdefConditionOrdinaryExportKobe(t);
  const colDefExportNsc: ColDef[] = useColdefConditionOrdinaryExportNsc(t);
  const colDefExportJfe: ColDef[] = useColdefConditionOrdinaryExportJfe(t);
  const frameworkComponents = {
    actionsManageCondition: ActionsManageCondition,
  };

  // SIDE EFFECT
  // ================================================================================

  // 確認用
  // useEffect(() => {
  // }, []);

  /**
   * 受け取ったmaker, typeに応じてColDefを変更する
   */
  useEffect(() => {
    if (gridApi) {
      if (selectedBusinessType === BUSINESS_TYPE.DOMESTIC) {
        // 国内
        if (selectedSteelMaker === STEEL_MAKER.NIPPONSTEEL) {
          gridApi.setColumnDefs(colDefDomesticNsc);
        } else if (selectedSteelMaker === STEEL_MAKER.AICHISTEEL) {
          gridApi.setColumnDefs(colDefDomesticAichi);
        } else if (selectedSteelMaker === STEEL_MAKER.KOBESTEEL) {
          gridApi.setColumnDefs(colDefDomesticKobe);
        } else if (selectedSteelMaker === STEEL_MAKER.JFESTEEL) {
          gridApi.setColumnDefs(colDefDomesticJfe);
        } else {
          gridApi.setColumnDefs(colDef);
        }
      } else if (selectedBusinessType === BUSINESS_TYPE.EXPORT) {
        // 輸出
        if (selectedSteelMaker === STEEL_MAKER.NIPPONSTEEL) {
          gridApi.setColumnDefs(colDefExportNsc);
        } else if (selectedSteelMaker === STEEL_MAKER.AICHISTEEL) {
          gridApi.setColumnDefs(colDefExportAichi);
        } else if (selectedSteelMaker === STEEL_MAKER.KOBESTEEL) {
          gridApi.setColumnDefs(colDefExportKobe);
        } else if (selectedSteelMaker === STEEL_MAKER.JFESTEEL) {
          gridApi.setColumnDefs(colDefExportJfe);
        } else {
          gridApi.setColumnDefs(colDef);
        }
      } else {
        gridApi.setColumnDefs(colDef);
      }
    } else {
      // do nothing
    }
  }, [selectedSteelMaker, selectedBusinessType, gridApi]);

  /**
   * Loading, NoRowsのグリッド表示
   */
  useEffect(() => {
    if (gridApi) {
      if (isLoading) {
        gridApi.showLoadingOverlay();
      } else {
        if (selectedViewMode === CONDITION_VIEW_MODES.EDITING && condition?.detailOrdinary?.length) {
          gridApi.hideOverlay();
        } else if (selectedViewMode === CONDITION_VIEW_MODES.APPROVED && condition?.detailOrdinaryApprove?.length) {
          gridApi.hideOverlay();
        } else {
          gridApi.showNoRowsOverlay();
        }
      }
    } else {
      // do nothing
    }
  }, [gridApi, isLoading, selectedViewMode, condition]);

  // EVENT HANDLER
  // ================================================================================

  /**
   * グリッドが準備できたらAPIを利用できるようにする
   * @param params GridReadyEvent
   */
  const onGridReady = (params: GridReadyEvent) => {
    // console.log('condition ordinary grid ready');
    setGridApi(params.api);
  };

  /**
   * ダブルクリックで編集モードにする
   * @param params GridReadyEvent
   */
  const onRowDoubleClicked = (event: RowDoubleClickedEvent) => {
    // console.log(event);
    const data: ConditionDetailType = event.data;
    if (condition.conditionStatus === 'EDITING') {
      dispatch(setConditionOrdinary(data.id));
      dispatch(setDialogMode('UPDATE'));
      dispatch(openDialog());
    }
  };

  // VIEW
  // ================================================================================

  return (
    <>
      <div className="ag-theme-balham mt-4" style={{ height: '30vh' }}>
        <AgGridReact
          immutableData={true}
          gridOptions={gridOptions}
          defaultColDef={defaultColDef}
          columnDefs={columnDefs}
          rowData={condition ? condition.detailOrdinary : []}
          onGridReady={onGridReady}
          onRowDoubleClicked={onRowDoubleClicked}
          frameworkComponents={frameworkComponents}
        />
      </div>
    </>
  );
};

export default GridConditionOrdinary;
